<template>
  <div
    class="content-chart vertical layout center-justified center"
    :class="{
      'hide-chart': $store.state.slideActive.hideResult,
      'donut-block-zoom': !hasImage,
    }"
  >
    <div
      v-if="questionImageLayoutMode.length && questionImageLayoutMode !== 'image'"
      class="expand-icon"
      @click.stop="expandCollapseTrigger"
    >
      <i
        v-if="questionImageLayoutMode !== 'chart'"
        class="fas fa-expand-alt __icon text-2xl text-base-0 pt-1"
      ></i>
      <i
        v-if="questionImageLayoutMode === 'chart'"
        class="fas fa-compress-alt __icon text-2xl text-base-0 pt-1"
      ></i>
    </div>
    <canvas
      id="multiple-choice-chart-doughnut"
      v-show="checkDataChart()"
      @click.stop="_showChartPercentage($event)"
      ref="doughnutChart"
    />
    <!-- Legend for chart -->
    <div
      id="doughnut-chart-legend"
      class="chart-legend horizontal layout end center-justified"
      ref="chart-legend"
      v-if="!checkDataChart() || $store.state.slideActive.hideResult"
    >
      <ul class="chart-legend-ul">
        <li
          class="label-list"
          v-for="(label,index) in $store.state.slideActive.SlideOptions"
          :key="index"
        >
          <div
            class="label-color"
            v-if="chartData.data"
            :style="{backgroundColor: defaultColor[index]}"
          />
          <span class="label-text">{{ label.title }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import Chart from 'chart.js'
import * as Sentry from '@sentry/vue'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { setTimeout } from 'timers'
import { mapState, mapActions, mapGetters } from 'vuex'
import Constant from '@/constant'
import Util from '@/util'
import Socket from '@/socket'

// api
import SlideApi from '@/api/slide'

Chart.plugins.unregister(ChartDataLabels)

const MultipleChoiceChartDoughnut = 'multiple-choice-chart-doughnut'

export default {
  props: {
    questionImageLayoutMode: {
      type: String,
      default: '',
    },
    hasImage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      text: '',
      isLoading: false,
      hideChart: false,
      originalLabels: [],
      valueFontSize: 0,
      chartData: {},
      myChart: null,
      defaultColor: [],
    }
  },
  computed: {
    ...mapGetters('presentationAttributeModule', ['presentationColorPalette', 'presentationLighterColorPalette']),

    ...mapState({
      slideActive: (state) => state.slideActive,
    }),
  },
  watch: {
    '$store.state.slideActive': function watchSlideActive() {
      this.updateData()
      setTimeout(() => {
        if (this.$refs['slide-imgg'] && !this.$refs['slide-imgg'].complete) {
          this.isLoading = true
        }
      }, 1)
    },
    '$store.getters.isPresenting': function watchIsPresenting() {
      this.updateData()
    },
    '$store.state.presentation.fontFamily': function watchFontFamily() {
      this.updateData()
    },
  },
  mounted() {
    if (this.$store.state.sharePresentation) {
      this.updateSlideActiveAction({
        hideResult: false,
      })
    }
    this.defaultColor = JSON.parse(JSON.stringify(this.presentationColorPalette))
    this.formatChartData()
    this.createChart(MultipleChoiceChartDoughnut, this.chartData)
    this.$store.state.tempAudienceCount = this.$store.state.slideActive.multipleCountAnswer
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction', 'setSlideActiveAction']),
    checkDataChart() {
      // Only show labels with value != 0
      let check = false
      this.$store.state.slideActive.SlideOptions.forEach((item) => {
        if (item.count) check = true
      })
      if (
        this.$store.state.slideActive.SlideOptions.every((option) => !option.id)
      ) check = true
      return check
    },
    formatChartData() {
      const self = this
      this.data = this.getData()
      this.chartData = {
        type: 'doughnut',
        data: {
          labels: self.data.labels,
          datasets: [
            {
              label: '',
              backgroundColor: self.getData().backgroundColor,
              hoverBackgroundColor: self.getData().backgroundColor,
              hoverBorderColor: self.getData().backgroundColor,
              pointBackgroundColor: 'white',
              data: self.getData().values,
            },
          ],
        },
        options: {
          onHover(e) {
            const activePoints = self.myChart.getElementsAtEvent(e)
            if (self.$refs.doughnutChart) {
              if (!activePoints.length) {
                self.$refs.doughnutChart.style.cursor = 'default'
              } else {
                self.$refs.doughnutChart.style.cursor = 'pointer'
              }
            }
          },
          plugins: {
            // Change options for ALL labels of THIS CHART
            datalabels: {
              labels: {
                labelsOutside: {
                  align: 'end',
                  anchor: 'end',
                  borderRadius: 4,
                  borderWidth: 1,
                  color: self.getData().textColor,
                  font: {
                    size: self.data.labelFontSize,
                    family: self.getData().fontFamily,
                  },
                  offset: 15,
                  display: 'true',
                  padding: 0,
                  formatter(value, context) {
                    return context.chart.data.labels[context.dataIndex]
                  },
                },
                valuesInside: {
                  align: 'center',
                  borderRadius: 4,
                  borderWidth: 1,
                  color: '#ffffff',
                  font: {
                    size: self.data.valueFontSize,
                    family: self.getData().fontFamily,
                  },
                  display: 'true',
                  padding: 0,
                  formatter(value) {
                    // Calculate total this way to avoid total = 0 when first create chart
                    if (self.$store.state.slideActive.showPercentage) {
                      if (value < 5) {
                        // eslint-disable-next-line no-return-assign
                        return value = ''
                      }
                      return `${value}%`
                    }
                    return value
                  },
                },
              },
            },
          },
          layout: {
            padding: self.getData().padding,
          },
          responsive: true,
          legend: {
            display: false,
          },
          tooltips: {
            enabled: true,
            mode: 'single',
            titleFontSize: 15,
            callbacks: {
              title(tooltipItem) {
                const tooltip = self.getData().originalLabels[
                  tooltipItem[0].index
                ]
                let tooltipCheck = ''

                if (tooltip) {
                  tooltipCheck = tooltip.replace(/\s/g, '')
                }
                if (!tooltipCheck || tooltip == 'undefined') {
                  return '(Empty)'
                }
                return self.getData().originalLabels[tooltipItem[0].index]
              },
              label(tooltipItem) {
                // Calculate total this way to avoid total = 0 when first create chart
                const value = self.getData().values[tooltipItem.index]
                if (self.$store.state.slideActive.showPercentage) {
                  return `${value}%`
                }
                return value
              },
            },
          },
          maintainAspectRatio: false,
          animation: {
            duration: 800,
          },
        },
      }
    },
    reRenderChart() {
      if (this.myChart) this.myChart.destroy()
      setTimeout(() => {
        this.createChart(MultipleChoiceChartDoughnut, this.chartData)
      }, 100)
    },
    createChart(chartId, chartData) {
      const ctx = document.getElementById(chartId)
      if (!ctx) return
      // Save reference
      this.myChart = new Chart(ctx, {
        plugins: [ChartDataLabels],
        type: chartData.type,
        data: chartData.data,
        options: chartData.options,
      })
    },

    getLimitLabels() {
      const originalLabels = []
      const limitLabel = 6
      let arr = []
      let options = JSON.parse(
        JSON.stringify(this.$store.state.slideActive.SlideOptions),
      )
      const colors = JSON.parse(JSON.stringify(this.presentationColorPalette))
      const arrColors = []
      options.map((item, key) => {
        // eslint-disable-next-line no-return-assign
        return (item.index = key)
      })
      options = options.filter((item) => {
        return item.count > 0
      })

      let countOther = 0
      if (options.length > limitLabel) {
        options.sort((a, b) => {
          return b.count - a.count
        })

        options.forEach((item, key) => {
          if (key < limitLabel) {
            arr.push(item)
            arrColors.push(colors[item.index])
            originalLabels.push(item.title)
          } else if (key > 29) {
            arr.push(item)
            arrColors.push(colors[item.index % 30])
            originalLabels.push(item.title)
          } else if (key >= limitLabel) {
            countOther += item.count
          }
        })

        if (countOther) {
          arr.push({
            title: 'Other',
            count: countOther,
            other: true,
            index: 29,
            id: 999999,
          })
          arrColors.push(colors[29])
          originalLabels.push('Other')
        }
      } else {
        arr = JSON.parse(JSON.stringify(options))
        arr.forEach((item) => {
          originalLabels.push(item.title)
          arrColors.push(colors[item.index])
        })
      }
      return {
        options: arr,
        colors: arrColors,
        originalLabels,
      }
    },

    getData() {
      this.hideChart = false
      let labels = []
      let values = []
      let totalValues = 0
      let isCheckData = false
      const optionFomarted = this.getLimitLabels()
      let { colors } = optionFomarted
      const { options } = optionFomarted
      options.forEach((item, key) => {
        if (key > 29) return
        if (!item.count) item.count = 0
        if (item.id) {
          let { title } = item
          if (
            this.$store.state.slideActive.addCorrectOption &&
            this.$store.state.slideActive.showCorrectOption &&
            item.correct
          ) {
            title = `✅ ${title}`
          }
          if (
            this.$store.state.slideActive.addCorrectOption &&
            this.$store.state.slideActive.showCorrectOption &&
            !item.correct
          ) {
            if (!item.other) {
              title = `❌ ${title}`
            }
            colors[key] = Util.changeRgbaColor(colors[key])
          }
          labels.push(title)
          values.push(item.count)
          totalValues += item.count
          isCheckData = true
        }
      })

      // Create new slide with no value
      if (values.length == 0) {
        for (let i = 0; i < 3; i++) {
          values[i] = 1
        }
        totalValues = values.reduce((a, b) => a + b, 0)
      }

      if (!isCheckData) {
        labels = JSON.parse(JSON.stringify(Constant.chartDefaultData.labels))
        values = JSON.parse(JSON.stringify(Constant.chartDefaultData.values))
        colors = JSON.parse(JSON.stringify(this.presentationColorPalette))
      }
      // If hide result, all values = 0, not show labels
      if (this.$store.state.slideActive.hideResult) {
        for (let i = 0; i < values.length; i++) {
          values[i] = 0
        }
      }
      const valueInPercentage = []
      values.forEach((value) => {
        valueInPercentage.push((value / totalValues) * 100)
      })
      const dataConfig = Util.getLabelPieChart(
        labels,
        this.$store.state.slideActive,
        values,
        valueInPercentage,
        this.$store.state.slideActive.showPercentage,
      )
      if (this.$store.state.slideActive.showPercentage) {
        for (let i = 0; i < valueInPercentage.length; i++) {
          if (valueInPercentage[i] % 1 === 0) {
            values[i] = valueInPercentage[i]
          } else {
            values[i] = Number(`${Math.round(`${valueInPercentage[i]}e1`)}e-1`)
          }
        }
      }
      let fontFamily = 'Plus Jakarta Sans'
      if (this.$store.state.presentation.fontFamily && this.$store.state.presentation.fontFamily != 'Plus Jakarta Sans') {
        const objectFont = Constant.fonts[this.$store.state.presentation.fontFamily]
        if (objectFont) {
          fontFamily = objectFont.value
        } else {
          fontFamily = this.$store.state.presentation.fontFamily
        }
        // fontFamily = Constant.fonts[this.$store.state.presentation.fontFamily].value
      }
      return {
        labels: dataConfig.labels,
        values,
        totalValues,
        backgroundColor: colors,
        labelFontSize: dataConfig.labelFontSize,
        padding: dataConfig.padding,
        valueFontSize: dataConfig.valueFontSize,
        originalLabels: optionFomarted.originalLabels,
        textColor: dataConfig.fontColor,
        fontFamily,
      }
    },

    updateData() {
      // Use reference
      if (!this.myChart) {
        setTimeout(() => {
          this.formatChartData()
          this.createChart(MultipleChoiceChartDoughnut, this.chartData)
        }, 1)

        return
      }
      if (this.myChart) {
        if (!this.checkDataChart()) return
        const data = this.getData()
        // Data to update
        this.myChart.data.datasets.forEach((dataset) => {
          dataset.data = data.values
        })
        this.myChart.data.labels = data.labels
        this.myChart.data.datasets[0].backgroundColor = data.backgroundColor
        this.myChart.data.datasets[0].hoverBackgroundColor = data.backgroundColor
        this.myChart.data.datasets[0].hoverBorderColor = data.backgroundColor
        const chartDatalabels = this.myChart.options.plugins.datalabels.labels
        if (chartDatalabels) {
          chartDatalabels.labelsOutside.font.size = data.labelFontSize
          chartDatalabels.valuesInside.font.size = data.valueFontSize
          chartDatalabels.labelsOutside.font.family = data.fontFamily
          chartDatalabels.valuesInside.font.family = data.fontFamily
          chartDatalabels.labelsOutside.color = data.textColor
        }
        this.myChart.options.layout.padding = data.padding
        if (!document.getElementById(MultipleChoiceChartDoughnut)) {
          this.reRenderChart()
        }
        this.myChart.update()
        if (this.myChart.data.datasets[0].data.every((data) => data === 0)) {
          this.hideChart = true
        } else {
          this.hideChart = false
        }
      }
    },
    _showChartPercentage(e) {
      const activePoints = this.myChart.getElementsAtEvent(e)
      if (!activePoints.length) {
        return
      }
      if (!this.$store.state.sharePresentation) {
        this.updateSlideActiveAction({
          showPercentage: !this.slideActive.showPercentage,
        })
        this._updateSlide({
          accessCode: this.$store.state.presentation.uniqueAccessCode,
          showPercentage: this.$store.state.slideActive.showPercentage,
        })
      }
    },

    async _updateSlide(params) {
      if (this.$store.state.sharePresentation) {
        return
      }
      this.setSlideActiveAction(this.slideActive)

      params.socketId = Socket.getSocketId()
      params.presenting = this.$store.getters.isPresenting
      this.setSyncLoadingAction(1)
      try {
        await SlideApi.updateSlideById(this.slideActive.id, params)
        Util.uploadSlide(
          this.$store.state.slideActive,
          this.$store.state.presentation,
        )
        Util.mixpanelTracking(
          'Slide',
          'Edit setting',
          'Presenter edit setting show percentage on chart',
        )
        this.setSyncLoadingAction(2)
      } catch (error) {
        Sentry.captureException(error)
        this.setSyncLoadingAction(3)
      }
    },
    expandCollapseTrigger() {
      if (this.questionImageLayoutMode !== 'chart') this.$emit('expandCollapseTrigger', 'chart')
      else if (this.questionImageLayoutMode === 'chart') this.$emit('expandCollapseTrigger', 'default')
    },
  },
}
</script>

<style lang="scss" scoped>
.content-chart {
  height: 100%;
  width: 100%;
  .expand-icon {
    @apply ml-auto w-10 h-10 text-center absolute rounded top-12 right-12 cursor-pointer;
    background-color: #ffffff;
    filter: drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.12));
    opacity: 0;
    i {
      @apply text-base-100;
    }
    &:hover {
      i {
        @apply text-primary-100;
      }
    }
  }
  &:hover {
    background: rgba(209, 209, 209, 0.2);
    .expand-icon {
      opacity: 1;
    }
  }
}

.chart-legend {
  width: 90%;
  height: 100%;
  margin: 0 auto;
  overflow-y: auto;
  -ms-overflow-style: auto;
  .chart-legend-ul {
    height: 90%;
    overflow-y: auto;
    -ms-overflow-style: auto;
    text-align: left;
    padding-left: 0;
    margin-bottom: 0;
    .label-list {
      display: inline-flex;
      align-items: center;
      .label-color {
        min-width: 12px;
        min-height: 12px;
        border-radius: 50%;
        display: inline-block;
      }
      .label-text {
        margin: 0 15px;
        text-align: left;
      }
    }
  }
}

.donut-block-zoom {
  &:hover {
    background: unset !important;
  }
  .expand-icon {
    opacity: 0 !important;
  }
}
.hide-chart {
  canvas {
    display: none !important;
  }
}
</style>
